

import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Edit3,
  Loader2,
  Save,
  AlertCircle,
  RefreshCw,
} from "lucide-react";

import {
  useGetEnglishQuestionByIdQuery,
  useUpdateEnglishQuestionMutation,
} from "../../slices/englishQuestionApiSlice";

import EnglishQuestionForm from "./components/EnglishQuestionForm";

const EnglishQuestionEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    data: question,
    isLoading: isQuestionLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetEnglishQuestionByIdQuery(id, {
    skip: !id,
  });

  const [updateEnglishQuestion, { isLoading: isUpdating }] =
    useUpdateEnglishQuestionMutation();

  const handleSubmit = async (formData) => {
    if (!id) return;

    try {
      await updateEnglishQuestion({
        id,
        ...formData,
      }).unwrap();

      navigate("/english-questions");
    } catch (err) {
      console.error("Failed to update English question:", err);
    }
  };

  const handleCancel = () => {
    navigate("/english-questions");
  };

  const handleBack = () => {
    navigate("/english-questions");
  };

  // =====================================================
  // Loading
  // =====================================================

  if (isQuestionLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-slate-500">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />

              <p className="text-sm font-medium">
                Loading English question...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // =====================================================
  // Error
  // =====================================================

  if (isError || !question) {
    const errorMessage =
      error?.data?.message ||
      error?.error ||
      "Unable to load the English question.";

    return (
      <div className="min-h-screen bg-slate-50">
        <div className="border-b border-slate-200 bg-white">
          <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to English Questions
            </button>
          </div>
        </div>

        <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="rounded-xl border border-red-200 bg-white p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>

              <div className="min-w-0 flex-1">
                <h2 className="text-base font-semibold text-slate-900">
                  Unable to load question
                </h2>

                <p className="mt-1 text-sm text-slate-600">
                  {errorMessage}
                </p>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Try Again
                  </button>

                  <button
                    type="button"
                    onClick={handleBack}
                    className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                  >
                    Back
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // =====================================================
  // Main Edit Page
  // =====================================================

  return (
    <div className="min-h-screen bg-slate-50">
      {/* =================================================
          Header
      ================================================== */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            {/* Left */}
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={handleBack}
                disabled={isUpdating}
                className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Back to English questions"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>

              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="truncate text-xl font-bold text-slate-900 sm:text-2xl">
                    Edit English Question
                  </h1>

                  {isFetching && (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-indigo-600" />
                  )}
                </div>

                <p className="mt-1 text-sm text-slate-500">
                  Update the question content, answer, explanation,
                  classification and marks.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isUpdating}
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                form="english-question-form"
                disabled={isUpdating}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* =================================================
          Main Content
      ================================================== */}
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          {/* Form Header */}
          <div className="border-b border-slate-200 px-5 py-4 sm:px-6">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50">
                <Edit3 className="h-4 w-4 text-indigo-600" />
              </div>

              <div>
                <h2 className="text-base font-semibold text-slate-900">
                  Question Details
                </h2>

                <p className="mt-1 text-sm text-slate-500">
                  Modify the English question information below.
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="p-5 sm:p-6">
            <EnglishQuestionForm
              initialData={question}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isLoading={isUpdating}
              mode="edit"
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default EnglishQuestionEdit;

